/**
 * A loyalty coupon is created when a customer redeems their loyalty points for a reward. Creating a loyalty coupon
 * also creates a corresponding "reference" coupon with the Coupons API.
 */
export interface LoyaltyCoupon {
  /**
   * Loyalty coupon ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.
   * @format GUID
   * @readonly
   */
  accountId?: string;
  /**
   * Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @format GUID
   * @readonly
   * @deprecated Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @replacedBy member_id
   * @targetRemovalDate 2024-06-01
   */
  memberIdDeprecated?: string;
  /**
   * Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @format GUID
   * @readonly
   */
  memberId?: string | null;
  /**
   * [Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.
   * @format GUID
   * @readonly
   */
  transactionId?: string | null;
  /**
   * Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)
   * that is created along with the loyalty coupon.
   * @readonly
   */
  couponReference?: CouponReference;
  /**
   * Loyalty coupon status.
   *
   * This status relates to the corresponding coupon that is created
   * at the same time as the loyalty coupon and is included in `couponReference`.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Name of reward that was redeemed to create this coupon.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  rewardName?: string;
  /**
   * Revision number, which increments by 1 each time the loyalty coupon is updated.
   *
   * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
   * @immutable
   */
  revision?: string | null;
  /**
   * Date and time the loyalty coupon was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the loyalty coupon was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.
   * @readonly
   */
  refundable?: boolean | null;
}

export interface CouponReference {
  /**
   * Coupon ID.
   * @format GUID
   * @readonly
   */
  couponId?: string;
  /**
   * Coupon code.
   *
   * Unique code entered by a customer to apply the coupon.
   * @maxLength 20
   * @readonly
   */
  code?: string;
  /**
   * Name of coupon.
   * @maxLength 80
   * @readonly
   */
  name?: string | null;
  /**
   * The information to use when creating the coupon.
   * @readonly
   */
  specification?: Specification;
  /**
   * Whether the referenced coupon was deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export interface Specification
  extends SpecificationTypeDetailsOneOf,
    SpecificationScopeOrMinSubtotalOneOf {
  /**
   * Fixed price discount.
   * @min 0.01
   */
  moneyOffAmount?: number;
  /** Discount as a percentage. */
  percentOffRate?: number;
  /** Free shipping. If true, the coupon applies to all items in all `namespaces` on a site. */
  freeShipping?: boolean;
  /** Specific sale price. Currently only supported for coupons with a `stores` `namespace`. */
  fixedPriceAmount?: number;
  /**
   * Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the
   * following scenario: if a visitor purchases x number of products, they receive y number of products for free. C
   * urrently only supported for coupons with a `stores` `namespace`.
   */
  buyXGetY?: BuyXGetY;
  /**
   * Scope of the coupon. When no scope is defined, the coupon applies to all
   * items in all `namespaces` in the site.
   */
  scope?: Scope;
  /** The coupon is only applicable when the order subtotal is over this amount. */
  minimumSubtotal?: number | null;
  /** Name of coupon. */
  name?: string | null;
  type?: TypeWithLiterals;
  /**
   * Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items
   * that the coupon applies to, only the lowest priced item is discounted.
   * Coupons with a `bookings` `namespace` are always limited to 1 item.
   */
  limitedToOneItem?: boolean | null;
  /** Whether the coupon also applies to subscriptions. */
  appliesToSubscriptions?: boolean | null;
  /**
   * Specifies the amount of cycles to apply the discount to for a subscription item.
   *
   * Can only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.
   * If `discountedCycleCount` is empty, the coupon applies to all available cycles.
   *
   * Min: `1`
   *
   * Max: `999`
   */
  discountedCycleCount?: number | null;
}

/** @oneof */
export interface SpecificationTypeDetailsOneOf {
  /**
   * Fixed price discount.
   * @min 0.01
   */
  moneyOffAmount?: number;
  /** Discount as a percentage. */
  percentOffRate?: number;
  /** Free shipping. If true, the coupon applies to all items in all `namespaces` on a site. */
  freeShipping?: boolean;
  /** Specific sale price. Currently only supported for coupons with a `stores` `namespace`. */
  fixedPriceAmount?: number;
  /**
   * Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the
   * following scenario: if a visitor purchases x number of products, they receive y number of products for free. C
   * urrently only supported for coupons with a `stores` `namespace`.
   */
  buyXGetY?: BuyXGetY;
}

/** @oneof */
export interface SpecificationScopeOrMinSubtotalOneOf {
  /**
   * Scope of the coupon. When no scope is defined, the coupon applies to all
   * items in all `namespaces` in the site.
   */
  scope?: Scope;
  /** The coupon is only applicable when the order subtotal is over this amount. */
  minimumSubtotal?: number | null;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  MONEY_OFF_AMOUNT = 'MONEY_OFF_AMOUNT',
  PERCENT_OFF_RATE = 'PERCENT_OFF_RATE',
  FREE_SHIPPING = 'FREE_SHIPPING',
  FIXED_PRICE_AMOUNT = 'FIXED_PRICE_AMOUNT',
  BUY_X_GET_Y = 'BUY_X_GET_Y',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'MONEY_OFF_AMOUNT'
  | 'PERCENT_OFF_RATE'
  | 'FREE_SHIPPING'
  | 'FIXED_PRICE_AMOUNT'
  | 'BUY_X_GET_Y';

export interface BuyXGetY {
  /** Number of purchased items required to receive free items. */
  x?: number;
  /** Number of items received for free if required number of items were purchased. */
  y?: number;
}

export interface Scope {
  /**
   * Group within a `namespace` for which the coupon is applicable.
   *
   * If no group is specified, the coupon applies to all items in the namespace.
   * `group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)
   * for a list of currently supported groups for each namespace.
   */
  name?: string | null;
  /**
   * ID of the specific entity in the group for which the coupon is applicable.
   *
   * If no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,
   * an `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)
   * for a list of currently supported entities for each namespace and group.
   */
  entityId?: string | null;
  /**
   * Wix application for which the coupon is applicable.
   *
   * One of the following:
   * + `"stores"`
   * + `"bookings"`
   * + `"events"`
   * + `"pricingPlans"`
   */
  namespace?: string;
}

export enum Status {
  /** Unknown status. */
  UNKNOWN = 'UNKNOWN',
  /** The reference coupon was created but the loyalty points have not been redeemed yet. */
  PENDING = 'PENDING',
  /** The reference coupon is active and available to the customer. */
  ACTIVE = 'ACTIVE',
  /** The reference coupon was applied and is no longer available for use. */
  APPLIED = 'APPLIED',
  /** The reference coupon was created but something went wrong when redeeming points from the loyalty account. */
  FAILED = 'FAILED',
  /** The reference coupon was deleted. */
  ARCHIVED = 'ARCHIVED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UNKNOWN'
  | 'PENDING'
  | 'ACTIVE'
  | 'APPLIED'
  | 'FAILED'
  | 'ARCHIVED';

export interface RedeemPointsForCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId: string;
  /**
   * ID of the loyalty account of the customer redeeming points. See the Loyalty Accounts API for more information.
   * @format GUID
   */
  loyaltyAccountId: string;
}

export interface RedeemPointsForCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
}

export interface RedeemCurrentMemberPointsForCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId: string;
}

export interface RedeemCurrentMemberPointsForCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
}

export interface RedeemMemberPointsForDiscountAmountCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId?: string;
  /**
   * Loyalty account ID. See the Loyalty Accounts API for more information.
   * @format GUID
   */
  loyaltyAccountId?: string;
  /** Number of points to redeem. */
  pointsToRedeem?: number;
  /** Coupon specification. */
  specification?: Specification;
}

export interface RedeemMemberPointsForDiscountAmountCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
  /** Transaction ID of the coupon redemption. */
  transactionId?: string;
}

export interface GetLoyaltyCouponRequest {
  /**
   * ID of the loyalty coupon to retrieve.
   * @format GUID
   */
  loyaltyCouponId: string;
}

export interface GetLoyaltyCouponResponse {
  /** Retrieved loyalty coupon. */
  loyaltyCoupon?: LoyaltyCoupon;
}

export interface BulkGetLoyaltyCouponRequest {
  /** Query to filter loyalty coupons. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface BulkGetLoyaltyCouponResponse {
  /**
   * Retrieved loyalty coupons.
   * @maxSize 100
   */
  couponsInSite?: CouponsInSite[];
}

export interface CouponsInSite {
  /**
   * Metasite ID.
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Retrieved loyalty coupons.
   * @maxSize 100
   */
  loyaltyCoupons?: LoyaltyCoupon[];
}

export interface GetCurrentMemberCouponsRequest {}

export interface GetCurrentMemberCouponsResponse {
  /** Retrieved loyalty coupons. */
  loyaltyCoupons?: LoyaltyCoupon[];
}

export interface QueryLoyaltyCouponRequest {
  /** Query options. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryLoyaltyCouponResponse {
  /** Retrieved loyalty coupons. */
  loyaltyCoupons?: LoyaltyCoupon[];
  /** Metadata. */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DeleteLoyaltyCouponRequest {
  /**
   * ID of the loyalty coupon to delete.
   * @format GUID
   */
  id: string;
  /**
   * Revision number, which increments by 1 each time the loyalty coupon is updated.
   *
   * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
   */
  revision?: string;
}

export interface DeleteLoyaltyCouponResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
